import { useContext, useEffect, useRef } from 'react';
import {
  getCurrencyFormat,
  getWarrantyTerm,
  formatUnixTime,
  LabelledItemShape,
  parseId,
  LabelledItemList,
} from '@rabbit/elements/shared-components';
import { WarrantyStatus } from '@rabbit/data/types';
import { getProductTitleFromHoldingData, stripTags } from '../utils/helpers';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import voidSVG from '../assets/images/Void.svg';
import { BL_Warranty } from '@rabbit/bizproc/core';
import { AppContext } from '@rabbit/app-context';
import { CaseflowContext } from '../context/CaseflowContext';
import { OurDateTime } from '@rabbit/utils/ts';

export function ClaimDetailPrint() {
  const { t } = useTranslation();
  const { caseFacts, holdingData, holdingProxyData, caseId, caseTimeCreated } =
    useContext(CaseflowContext) || {};
  const { tenantInfo, config } = useContext(AppContext);
  const latestWarranty = BL_Warranty.getLatestWarranty(
    holdingProxyData?.warranties
  );

  const warrantyInfo = BL_Warranty.getLatestWarranty(holdingData?.warranties);
  const voided = warrantyInfo?.status === WarrantyStatus.VOIDED;
  const printRef = useRef<HTMLDivElement>(null);
  const isWithinWarranty = () => {
    if (!holdingProxyData) return '-';

    if (!latestWarranty || !latestWarranty.endDate) return '-';

    return OurDateTime.nowUTCTimestamp() < latestWarranty?.endDate
      ? 'Yes'
      : 'No';
  };

  useEffect(() => {
    if (holdingData) {
      // Delay the print to allow logo to load
      setTimeout(() => {
        window.print();
      }, 1000);
    }
  }, [holdingData]);

  const productItems: LabelledItemShape[] = holdingData
    ? [
        {
          label: t('general.id'),
          value: holdingProxyData?.docid?.slice(0, 5).toUpperCase() ?? '-',
        },
        {
          label: t('general.product'),
          value: getProductTitleFromHoldingData(holdingData),
        },
        {
          label: t('general.purchasePrice'),
          value: getCurrencyFormat(
            caseFacts?.consumer_holding_purchase_price
              ? (caseFacts?.consumer_holding_purchase_price
                  .amount as unknown as string)
              : '0',
            caseFacts?.consumer_holding_purchase_price?.currency ??
              tenantInfo?.currency ??
              'GBP'
          ),
        },
        ...(config.CLAIMS.CLAIMS_FLOW.NEW_CLAIM.SERIAL_NUMBER
          ? [
              {
                key: 'serial_number',
                label: t('general.serialNumber'),
                value: caseFacts?.consumer_holding_serial_number
                  ? caseFacts.consumer_holding_serial_number
                  : caseFacts?.serial_number
                  ? caseFacts.serial_number
                  : '-',
              },
            ]
          : []),
      ]
    : [];

  const claimDetailItems: LabelledItemShape[] = [
    {
      label: t('general.claimId'),
      value: caseId
        ? `${
            caseFacts?.warranty_status
              ? caseFacts?.warranty_status.toLowerCase() === 'in warranty'
                ? 'WR-'
                : 'OR-'
              : ''
          }${parseId(caseId)}`
        : '-',
    },
    {
      label: t('general.dateOfClaim'),
      value: caseTimeCreated
        ? formatUnixTime(Number(caseTimeCreated), 'dd/MM/yyyy')
        : '-',
    },
    {
      label: t('general.purchaseDate'),
      value:
        holdingData?.purchase_time && holdingData?.purchase_time > 0
          ? formatUnixTime(holdingData?.purchase_time, 'dd/MM/yyyy')
          : '-',
    },
    {
      label: t('general.warrantyTerm'),
      value: holdingData?.warranty_term?.amount
        ? `${holdingData?.warranty_term?.amount} ${holdingData?.warranty_term?.division}`
        : '-',
    },
    {
      label: t('general.warrantyExpiry'),
      value:
        latestWarranty?.endDate && latestWarranty?.endDate > 0
          ? formatUnixTime(latestWarranty?.endDate, 'dd/MM/yyyy')
          : '-',
    },
    {
      label: t('general.withinWarrantyPeriod'),
      value: isWithinWarranty(),
    },
  ];

  const consumerItems: LabelledItemShape[] = [
    {
      label: t('general.name'),
      value: caseFacts?.consumer_name ?? '-',
    },
    {
      label: t('general.email'),
      value: caseFacts?.consumer_email ?? '-',
    },
    {
      label: t('general.issueType'),
      value: caseFacts?.consumer_issue_type ?? '-',
    },
    {
      label: t('general.issueDescription'),
      value: stripTags(caseFacts?.consumer_issue_description ?? '') ?? '-',
    },
  ];

  return (
    <div className="flex w-[800px] flex-col gap-3 py-6 " ref={printRef}>
      {voided && (
        <div className="fixed left-0 top-0 z-10 flex h-full w-full items-center justify-center">
          <img src={voidSVG} alt="void" className="-mt-[200px] w-[400px]" />
        </div>
      )}
      <div className="flex items-start justify-between">
        <div className="font-caudex text-2xl">Claim Summary</div>
        <div className="flex flex-col items-end justify-end gap-2 text-gray-600">
          <img
            src={tenantInfo?.logo}
            alt={tenantInfo?.name}
            className="mr-1 max-h-[40px] max-w-[220px]"
          />
          <div>{t('CFG_COBRAND_EMAIL')}</div>
        </div>
      </div>

      <div className="flex flex-col gap-5">
        {[
          { label: 'Customer detail', items: consumerItems },
          { label: 'Product detail', items: productItems },
          { label: 'Claim detail', items: claimDetailItems },
        ].map(
          (item, index) =>
            item.items && (
              <div
                className="rounded-md border border-gray-300 bg-white py-2 px-3"
                key={'print-item-' + index}
              >
                <div>
                  <div className="font-caudex mb-2 border-b border-gray-300 pb-2 text-lg">
                    {item.label}
                  </div>
                  <LabelledItemList items={item.items} />
                </div>
              </div>
            )
        )}
      </div>
    </div>
  );
}

export default ClaimDetailPrint;
